import { useTenant } from 'probonio-shared-ui/module/me';
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY_PREFIX = 'employeeListFilterActive';
const listeners = new Set<() => void>();

function storageKey(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}:${tenantId}`;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// defaults to true (filtering on) so the list looks like before the data pools were merged
function readActive(tenantId: string | undefined): boolean {
  if (!tenantId) {
    return true;
  }
  return localStorage.getItem(storageKey(tenantId)) !== 'false';
}

/**
 * Whether the tenant's employee list should hide employees flagged as hidden; persisted per tenant in
 * localStorage. Backed by an external store so every consumer (settings switch, in-list eye) stays in sync.
 */
export function useEmployeeListFilterActive(): [boolean, (active: boolean) => void] {
  const tenantId = useTenant().tenant?.id;
  const active = useSyncExternalStore(subscribe, () => readActive(tenantId));

  const setActive = useCallback(
    (value: boolean) => {
      if (tenantId) {
        localStorage.setItem(storageKey(tenantId), String(value));
        listeners.forEach(listener => listener());
      }
    },
    [tenantId],
  );

  return [active, setActive];
}
